<script setup>
import CaretLeftIcon from '@iconify-vue/ph/caret-left';
import CaretRightIcon from '@iconify-vue/ph/caret-right';
import DownloadSimpleIcon from '@iconify-vue/ph/download-simple';
import FileIcon from '@iconify-vue/ph/file';
import FileHtmlIcon from '@iconify-vue/ph/file-html';
import FilePdfIcon from '@iconify-vue/ph/file-pdf';
import MagnifyingGlassMinusIcon from '@iconify-vue/ph/magnifying-glass-minus';
import MagnifyingGlassPlusIcon from '@iconify-vue/ph/magnifying-glass-plus';
import PlayCircleIcon from '@iconify-vue/ph/play-circle';
import XIcon from '@iconify-vue/ph/x';
import { useRoute } from '@js/utils/route';
import { AnimatePresence, Motion } from 'motion-v';
import {
    DialogClose,
    DialogContent,
    DialogOverlay,
    DialogPortal,
    DialogRoot,
    DialogTitle,
    VisuallyHidden,
} from 'reka-ui';
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

const { files } = defineProps({
    files: {
        type: Array,
        required: true,
    },
});

const { route } = useRoute();

const selectedIndex = ref(null);
const selectedFile = computed(() =>
    selectedIndex.value !== null ? files[selectedIndex.value] : null,
);
const scale = ref(1);
const translateX = ref(0);
const translateY = ref(0);
const isDragging = ref(false);
const dragStart = ref({ x: 0, y: 0 });

const MIN_SCALE = 1;
const MAX_SCALE = 5;
const SCALE_STEP = 0.5;

const hasPrev = computed(
    () => selectedIndex.value !== null && selectedIndex.value > 0,
);
const hasNext = computed(
    () =>
        selectedIndex.value !== null && selectedIndex.value < files.length - 1,
);

function resetZoom() {
    scale.value = 1;
    translateX.value = 0;
    translateY.value = 0;
}

function goToPrev() {
    if (!hasPrev.value) return;
    resetZoom();
    selectedIndex.value--;
}

function goToNext() {
    if (!hasNext.value) return;
    resetZoom();
    selectedIndex.value++;
}

function onKeydown(e) {
    if (selectedIndex.value === null) return;
    if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrev();
    } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNext();
    }
}

onMounted(() => window.addEventListener('keydown', onKeydown));
onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown));

function zoomIn() {
    scale.value = Math.min(MAX_SCALE, scale.value + SCALE_STEP);
    if (scale.value === MIN_SCALE) {
        translateX.value = 0;
        translateY.value = 0;
    }
}

function zoomOut() {
    scale.value = Math.max(MIN_SCALE, scale.value - SCALE_STEP);
    if (scale.value === MIN_SCALE) {
        translateX.value = 0;
        translateY.value = 0;
    }
}

function onWheel(e) {
    e.preventDefault();
    if (e.deltaY < 0) {
        zoomIn();
    } else {
        zoomOut();
    }
}

function onPointerDown(e) {
    if (scale.value <= MIN_SCALE) return;
    isDragging.value = true;
    dragStart.value = {
        x: e.clientX - translateX.value,
        y: e.clientY - translateY.value,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
}

function onPointerMove(e) {
    if (!isDragging.value) return;
    translateX.value = e.clientX - dragStart.value.x;
    translateY.value = e.clientY - dragStart.value.y;
}

function onPointerUp() {
    isDragging.value = false;
}

function isImage(url) {
    const imageExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
    const extension = url.split('.').pop().toLowerCase().split('?')[0];
    return imageExtensions.includes(extension);
}

function isPdf(url) {
    const extension = url.split('.').pop().toLowerCase().split('?')[0];
    return extension === 'pdf';
}

function isVideo(url) {
    const videoExtensions = ['mp4', 'webm'];
    const extension = url.split('.').pop().toLowerCase().split('?')[0];
    return videoExtensions.includes(extension);
}

function isHtml(url) {
    const extension = url.split('.').pop().toLowerCase().split('?')[0];
    return extension === 'html';
}

function getFileName(url) {
    return url.split('/').pop().split('?')[0];
}

function download(url) {
    const link = document.createElement('a');
    link.href = route('download-proxy', { url });
    link.download = getFileName(url);
    link.target = '_blank';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
}

function openLightbox(index) {
    selectedIndex.value = index;
}

const isLightboxOpen = computed({
    get: () => selectedIndex.value !== null,
    set: (value) => {
        if (!value) {
            selectedIndex.value = null;
        }
    },
});

watch(isLightboxOpen, (open) => {
    if (!open) resetZoom();
});
</script>

<template>
    <div
        class="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5"
    >
        <div
            v-for="(file, index) in files"
            :key="index"
            class="group relative overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm transition-shadow hover:shadow-md"
        >
            <div
                class="flex aspect-square cursor-pointer items-center justify-center"
                @click="openLightbox(index)"
            >
                <img
                    v-if="isImage(file)"
                    :src="file"
                    :alt="getFileName(file)"
                    class="size-full object-cover"
                />

                <div v-else-if="isVideo(file)" class="relative size-full">
                    <video
                        :src="file"
                        preload="metadata"
                        muted
                        playsinline
                        class="size-full bg-black object-cover"
                    />

                    <div
                        class="absolute inset-0 flex items-center justify-center bg-black/30"
                    >
                        <PlayCircleIcon
                            class="size-12 text-white drop-shadow-lg"
                        />
                    </div>
                </div>

                <div
                    v-else-if="isPdf(file)"
                    class="flex flex-col items-center justify-center gap-2 p-4 text-center"
                >
                    <FilePdfIcon class="size-16 text-indigo-600" />

                    <span class="text-xs text-gray-600">
                        {{ getFileName(file) }}
                    </span>
                </div>

                <div
                    v-else-if="isHtml(file)"
                    class="flex flex-col items-center justify-center gap-2 p-4 text-center"
                >
                    <FileHtmlIcon class="size-16 text-orange-600" />

                    <span class="text-xs text-gray-600">
                        {{ getFileName(file) }}
                    </span>
                </div>

                <div
                    v-else
                    class="flex flex-col items-center justify-center gap-2 p-4 text-center"
                >
                    <FileIcon class="size-16 text-indigo-600" />

                    <span class="text-xs text-gray-600">
                        {{ getFileName(file) }}
                    </span>
                </div>
            </div>

            <button
                class="absolute top-2 right-2 aspect-square rounded-full bg-indigo-600 p-2 text-white opacity-0 shadow-lg transition-opacity duration-150 group-hover:opacity-100 hover:bg-indigo-700"
                @click.stop="download(file)"
                title="Télécharger"
            >
                <DownloadSimpleIcon class="size-5" />
            </button>
        </div>
    </div>

    <DialogRoot v-model:open="isLightboxOpen">
        <DialogPortal forceMount>
            <AnimatePresence>
                <Motion
                    v-if="isLightboxOpen"
                    key="overlay"
                    :initial="{ opacity: 0 }"
                    :animate="{ opacity: 1 }"
                    :exit="{ opacity: 0 }"
                    :transition="{ duration: 0.2 }"
                    class="fixed inset-0 z-50"
                >
                    <DialogOverlay forceMount class="size-full bg-black/80" />
                </Motion>

                <Motion
                    v-if="isLightboxOpen"
                    key="content"
                    :initial="{ opacity: 0, scale: 0.96 }"
                    :animate="{ opacity: 1, scale: 1 }"
                    :exit="{ opacity: 0, scale: 0.96 }"
                    :transition="{
                        type: 'spring',
                        duration: 0.3,
                        bounce: 0.1,
                    }"
                    class="pointer-events-none fixed inset-0 z-50 flex items-center justify-center"
                >
                    <DialogContent
                        forceMount
                        class="pointer-events-auto relative max-h-[90vh] max-w-[90vw] focus:outline-none"
                        :aria-describedby="undefined"
                    >
                        <VisuallyHidden>
                            <DialogTitle />
                        </VisuallyHidden>

                        <div
                            class="absolute -top-12 right-0 left-0 flex items-center justify-between"
                        >
                            <div class="flex gap-2">
                                <button
                                    class="aspect-square rounded-full bg-indigo-600 p-2 text-white shadow-lg duration-150 enabled:hover:bg-indigo-700"
                                    @click="download(selectedFile)"
                                    title="Télécharger"
                                >
                                    <DownloadSimpleIcon class="size-5" />
                                </button>

                                <template
                                    v-if="selectedFile && isImage(selectedFile)"
                                >
                                    <button
                                        class="aspect-square rounded-full bg-indigo-600 p-2 text-white shadow-lg duration-150 enabled:hover:bg-indigo-700 disabled:opacity-40"
                                        :disabled="scale <= 1"
                                        title="Dézoomer"
                                        @click="zoomOut"
                                    >
                                        <MagnifyingGlassMinusIcon
                                            class="size-5"
                                        />
                                    </button>

                                    <button
                                        class="aspect-square rounded-full bg-indigo-600 p-2 text-white shadow-lg duration-150 enabled:hover:bg-indigo-700 disabled:opacity-40"
                                        :disabled="scale >= 5"
                                        title="Zoomer"
                                        @click="zoomIn"
                                    >
                                        <MagnifyingGlassPlusIcon
                                            class="size-5"
                                        />
                                    </button>
                                </template>
                            </div>

                            <DialogClose
                                class="aspect-square rounded-full bg-indigo-600 p-2 text-white shadow-lg duration-150 hover:bg-indigo-700"
                            >
                                <XIcon class="size-5" />
                            </DialogClose>
                        </div>

                        <button
                            title="Précédent"
                            @click="goToPrev"
                            :disabled="!hasPrev"
                            class="absolute top-1/2 -left-12 z-50 aspect-square -translate-y-1/2 rounded-full bg-indigo-600 p-2 text-white shadow-lg duration-150 enabled:hover:bg-indigo-700 disabled:opacity-40"
                        >
                            <CaretLeftIcon class="size-5" />
                        </button>

                        <button
                            title="Suivant"
                            @click="goToNext"
                            :disabled="!hasNext"
                            class="absolute top-1/2 -right-12 z-50 aspect-square -translate-y-1/2 rounded-full bg-indigo-600 p-2 text-white shadow-lg duration-150 enabled:hover:bg-indigo-700 disabled:opacity-40"
                        >
                            <CaretRightIcon class="size-5" />
                        </button>

                        <template v-if="selectedFile">
                            <div
                                v-if="isImage(selectedFile)"
                                class="overflow-hidden rounded-lg shadow-lg"
                                @wheel="onWheel"
                                @dblclick="resetZoom"
                            >
                                <img
                                    :src="selectedFile"
                                    :alt="getFileName(selectedFile)"
                                    :style="{
                                        transform: `scale(${scale}) translate(${translateX / scale}px, ${translateY / scale}px)`,
                                    }"
                                    class="max-h-[85vh] max-w-[85vw] object-contain transition-transform duration-150"
                                    :class="{
                                        'cursor-grab': scale > 1 && !isDragging,
                                        'cursor-grabbing': isDragging,
                                        '!duration-0': isDragging,
                                    }"
                                    draggable="false"
                                    @pointerdown="onPointerDown"
                                    @pointermove="onPointerMove"
                                    @pointerup="onPointerUp"
                                />
                            </div>

                            <video
                                v-else-if="isVideo(selectedFile)"
                                :src="selectedFile"
                                controls
                                autoplay
                                playsinline
                                class="max-h-[85vh] max-w-[85vw] rounded-lg bg-black shadow-lg"
                            />

                            <iframe
                                v-else-if="isPdf(selectedFile)"
                                :src="selectedFile"
                                class="h-[85vh] w-[85vw] rounded-lg bg-white shadow-lg"
                            />

                            <iframe
                                v-else-if="isHtml(selectedFile)"
                                :src="selectedFile"
                                sandbox=""
                                class="h-[85vh] w-[85vw] rounded-lg bg-white shadow-lg"
                            />

                            <div
                                v-else
                                class="flex flex-col items-center justify-center gap-4 rounded-lg bg-white p-8 shadow-lg"
                            >
                                <FileIcon class="size-24 text-gray-400" />

                                <span class="text-lg text-gray-600">
                                    {{ getFileName(selectedFile) }}
                                </span>
                            </div>
                        </template>
                    </DialogContent>
                </Motion>
            </AnimatePresence>
        </DialogPortal>
    </DialogRoot>
</template>
